import { useState, useEffect } from 'react'
import { FolderTree, Plus, Sparkles, Wand2, Edit, Trash2 } from 'lucide-react'
import {
  PageHeader, Card, CardBody, Button, Badge, Modal, FormField, FormTextarea,
  FormSelect, LoadingSpinner, ErrorMessage, SuccessMessage, EmptyState, ConfirmationModal
} from '../components/common'
import { knowledgeBaseApi } from '../services/api'
import { extractErrorMessage } from '../utils/errorUtils'

function TopicNode({ topic, depth = 0, onEdit, onDelete }) {
  return (
    <>
      <div className="flex items-center justify-between py-2 px-3 hover:bg-slate-50 rounded-lg group"
        style={{ paddingLeft: `${depth * 24 + 12}px` }}>
        <div className="flex items-center gap-2 min-w-0">
          <FolderTree size={16} className="text-slate-400 flex-shrink-0" />
          <span className="text-sm font-medium text-slate-900 truncate">{topic.name}</span>
          {topic.isAiGenerated && <Badge variant="info" className="text-xs">AI</Badge>}
          <span className="text-xs text-slate-400">{topic.documentCount} doc(s)</span>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onEdit(topic)} className="p-1 text-slate-400 hover:text-slate-600">
            <Edit size={14} />
          </button>
          <button onClick={() => onDelete(topic)} className="p-1 text-slate-400 hover:text-red-500">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      {topic.children?.map(child => (
        <TopicNode key={child.id} topic={child} depth={depth + 1} onEdit={onEdit} onDelete={onDelete} />
      ))}
    </>
  )
}

export default function KnowledgeTopics() {
  const [topics, setTopics] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [editingTopic, setEditingTopic] = useState(null)
  const [formData, setFormData] = useState({ name: '', description: '', parentTopicId: '', sortOrder: 0 })
  const [saving, setSaving] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [autoAssigning, setAutoAssigning] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [topicToDelete, setTopicToDelete] = useState(null)

  const buildingId = localStorage.getItem('selectedBuildingId')

  useEffect(() => {
    if (buildingId) loadTopics()
  }, [buildingId])

  const loadTopics = async () => {
    try {
      setLoading(true)
      const response = await knowledgeBaseApi.getTopics(buildingId)
      setTopics(response.data || [])
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const flatTopics = (list, result = []) => {
    list.forEach(t => { result.push(t); if (t.children) flatTopics(t.children, result) })
    return result
  }

  const parentOptions = [
    { value: '', label: 'None (Root)' },
    ...flatTopics(topics)
      .filter(t => editingTopic ? t.id !== editingTopic.id : true)
      .map(t => ({ value: String(t.id), label: t.name }))
  ]

  const openCreateModal = () => {
    setEditingTopic(null)
    setFormData({ name: '', description: '', parentTopicId: '', sortOrder: 0 })
    setShowModal(true)
  }

  const openEditModal = (topic) => {
    setEditingTopic(topic)
    setFormData({
      name: topic.name,
      description: topic.description || '',
      parentTopicId: topic.parentTopicId ? String(topic.parentTopicId) : '',
      sortOrder: topic.sortOrder
    })
    setShowModal(true)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      if (editingTopic) {
        await knowledgeBaseApi.updateTopic(editingTopic.id, {
          name: formData.name,
          description: formData.description || null,
          parentTopicId: formData.parentTopicId ? parseInt(formData.parentTopicId) : null,
          sortOrder: parseInt(formData.sortOrder) || 0
        })
        setSuccess('Topic updated')
      } else {
        await knowledgeBaseApi.createTopic({
          buildingId,
          name: formData.name,
          description: formData.description || null,
          parentTopicId: formData.parentTopicId ? parseInt(formData.parentTopicId) : null,
          sortOrder: parseInt(formData.sortOrder) || 0
        })
        setSuccess('Topic created')
      }
      setShowModal(false)
      loadTopics()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!topicToDelete) return
    try {
      await knowledgeBaseApi.deleteTopic(topicToDelete.id)
      setShowDeleteModal(false)
      setTopicToDelete(null)
      setSuccess('Topic deleted')
      loadTopics()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(extractErrorMessage(err))
    }
  }

  const handleGenerate = async () => {
    try {
      setGenerating(true)
      setError(null)
      await knowledgeBaseApi.generateTopics(buildingId)
      setSuccess('Topics generated by AI')
      loadTopics()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setGenerating(false)
    }
  }

  const handleAutoAssign = async () => {
    try {
      setAutoAssigning(true)
      setError(null)
      await knowledgeBaseApi.autoAssignDocuments(buildingId)
      setSuccess('Documents auto-assigned to topics')
      loadTopics()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setAutoAssigning(false)
    }
  }

  if (loading) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      <PageHeader
        title="Topics"
        subtitle="Organize documents into topic categories"
        backLink={{ label: 'Back to Knowledge Base', href: '/knowledge-base' }}
      />

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}
      {success && <SuccessMessage message={success} />}

      <div className="flex flex-wrap gap-3">
        <Button variant="primary" icon={<Plus size={16} />} onClick={openCreateModal}>Create Topic</Button>
        <Button variant="secondary" icon={<Sparkles size={16} />} onClick={handleGenerate}
          loading={generating} disabled={generating}>
          {generating ? 'Generating...' : 'AI Generate Topics'}
        </Button>
        <Button variant="secondary" icon={<Wand2 size={16} />} onClick={handleAutoAssign}
          loading={autoAssigning} disabled={autoAssigning}>
          {autoAssigning ? 'Assigning...' : 'AI Auto-Assign'}
        </Button>
      </div>

      <Card>
        <CardBody>
          {topics.length === 0 ? (
            <EmptyState
              title="No topics yet"
              description="Create topics manually or let AI generate them from your documents"
              actionLabel="Create Topic"
              onAction={openCreateModal}
            />
          ) : (
            <div className="divide-y divide-slate-100">
              {topics.map(topic => (
                <TopicNode key={topic.id} topic={topic}
                  onEdit={openEditModal}
                  onDelete={(t) => { setTopicToDelete(t); setShowDeleteModal(true) }} />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}
        title={editingTopic ? 'Edit Topic' : 'Create Topic'} size="medium">
        <div className="space-y-4">
          <FormField label="Name" value={formData.name} required
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} />
          <FormTextarea label="Description" value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} rows={3} />
          <FormSelect label="Parent Topic" value={formData.parentTopicId}
            onChange={(e) => setFormData(prev => ({ ...prev, parentTopicId: e.target.value }))}
            options={parentOptions} />
          <FormField label="Sort Order" type="number" value={formData.sortOrder}
            onChange={(e) => setFormData(prev => ({ ...prev, sortOrder: e.target.value }))} />
          <div className="flex gap-3 pt-2">
            <Button variant="primary" onClick={handleSave} loading={saving}>
              {editingTopic ? 'Update' : 'Create'}
            </Button>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setTopicToDelete(null) }}
        onConfirm={handleDelete}
        title="Delete Topic"
        message={`Are you sure you want to delete "${topicToDelete?.name}"?`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  )
}
